/**
 * The Transfers chip's summary (UI/UX fix round A, review U13): a short label that never grows across the window, and
 * the full breakdown by state for its tooltip.
 */
import type { TransferJob, TransferJobState } from '../../../types/azureStorageTransfers';
import { isActiveTransferState } from './transferJobRules';

/** `label` stays short (U13: the chip must not grow across the window); `detail` is the full breakdown for its tooltip. */
export type TransfersAggregate = { activeCount: number; percent: number | undefined; label: string; detail: string };

const IDLE_ORDER: TransferJobState[] = ['completed', 'partial', 'failed', 'cancelled', 'interrupted'];

/** "2 transfers · 25 %" while anything is live; otherwise "5 transfers · 1 needs attention". */
export function aggregateTransfers(jobs: TransferJob[]): TransfersAggregate {
  const active = jobs.filter((job) => isActiveTransferState(job.state));
  if (active.length > 0) {
    const totals = active.reduce((acc, job) => ({ done: acc.done + job.bytesDone, total: acc.total + (job.bytesTotal ?? 0) }), { done: 0, total: 0 });
    const percent = totals.total > 0 ? Math.min(100, Math.floor((totals.done / totals.total) * 100)) : undefined;
    const allExports = active.every((job) => job.kind === 'export');
    const noun = allExports ? (active.length === 1 ? 'export' : 'exports') : active.length === 1 ? 'transfer' : 'transfers';
    const entityProgress = allExports ? ` · ${active.reduce((sum, job) => sum + job.itemsDone, 0)} entities` : '';
    const label = `${active.length} ${noun}${percent === undefined ? entityProgress : ` · ${percent} %`}`;
    return { activeCount: active.length, percent, label, detail: [label, idleBreakdown(jobs)].filter(Boolean).join(' · ') };
  }
  if (jobs.length === 0) return { activeCount: 0, percent: undefined, label: '', detail: '' };
  // U13: "5 transfers · 1 needs attention" — failed, partly completed and interrupted jobs need a look; the breakdown by
  // state is the tooltip.
  const attention = jobs.filter((job) => job.state === 'failed' || job.state === 'partial' || job.state === 'interrupted').length;
  const label = `${jobs.length} ${jobs.length === 1 ? 'transfer' : 'transfers'}${attention > 0 ? ` · ${attention} ${attention === 1 ? 'needs' : 'need'} attention` : ''}`;
  return { activeCount: 0, percent: undefined, label, detail: idleBreakdown(jobs) };
}

function idleBreakdown(jobs: TransferJob[]): string {
  return IDLE_ORDER.map((state) => ({ state, count: jobs.filter((job) => job.state === state).length }))
    .filter((entry) => entry.count > 0)
    .map((entry) => `${entry.count} ${entry.state === 'partial' ? 'partly completed' : entry.state}`)
    .join(' · ');
}

